/**
 * The chart card: the day's end as a picture (Q32.1), meant for pasting, and
 * the same data as JSON for the interface.
 *
 * The text form is the title, the island and its day, six rows of ten emoji
 * squares, the numbers as sentences, and the site's address. Each square is a
 * two-by-two block of the 20 x 12 island: dark where nothing was seen; one
 * colour while the island is up, because the map is the letter economy's
 * currency and the Guild does not publish it on a live island; the block's
 * terrain once the island has gone under and its seed is public anyway; and a
 * star where a block holds a relic the draw gave this player.
 *
 * ASCII plus the chart squares (Q7.2, amended by Q32.3). The map's glyphs fall
 * back to different fonts wherever the card is pasted, so none of them appear
 * here; the emoji squares are the one glyph set every chat client draws at one
 * width, which is why the exception is exactly them.
 *
 * The completion it quotes is the **live-day** figure, frozen at finalisation,
 * not the Atlas figure that replay can raise (`docs/rules.md` section 8), and
 * it is a share of the island's chartable tiles (Q32.4).
 *
 * Every word on the card comes in as a label. Core holds no sentences: the
 * caller resolves them from `guild-voice.json` in the reader's locale through
 * the i18n formatter. `shareCardLines` says which keys, with which arguments,
 * so the choice by state is made in one place; `SHARE_CARD_UI_KEYS` names them.
 */

import { z } from 'zod'
import type { Terrain } from './game-config.ts'
import { GAME_CONFIG } from './game-config.ts'
import { hasTile, index, type Position, type TileSet } from './tiles.ts'

// --- the chart ---------------------------------------------------------------

/** The chart is 10 x 6: twenty squares to a line wraps on a phone, ten fits. */
export const CHART_WIDTH = 10
export const CHART_HEIGHT = 6
export const CHART_CELLS = CHART_WIDTH * CHART_HEIGHT

/** Tiles of the island under one cell of the chart, along each axis. */
const BLOCK = GAME_CONFIG.grid.width / CHART_WIDTH

/**
 * The cell codes the JSON carries, so it holds no emoji and the client can
 * draw the chart however it likes: `.` unseen, `#` seen while the island is
 * up, a terrain letter once it has gone under, `*` a relic won.
 */
export const CHART_CODES = ['.', '#', 'w', 's', 'g', 'f', 'm', 'r', '*'] as const
export type ChartCode = (typeof CHART_CODES)[number]

/** The squares the text form draws each code as. */
export const CHART_SQUARES: Readonly<Record<ChartCode, string>> = {
  '.': '⬛',
  // One colour for a seen cell on a live island: the card says how much was
  // charted, never what.
  '#': '🟨',
  w: '🟦',
  s: '🟨',
  g: '🟩',
  f: '🟫',
  m: '⬜',
  r: '🟪',
  '*': '⭐',
}

const TERRAIN_CODE: Readonly<Record<Terrain, ChartCode>> = {
  water: 'w',
  sand: 's',
  grass: 'g',
  forest: 'f',
  mountain: 'm',
  ruin: 'r',
}

/** Rarest first, which is the order a tie between terrains is broken in. */
const RARER_FIRST: readonly Terrain[] = ['ruin', 'mountain', 'forest', 'grass', 'sand', 'water']

/**
 * Downsample a seen set to the sixty cell codes.
 *
 * `tiles` is the island's terrain once it has finalised, and `null` while it
 * is up: with no terrain to hand, a seen cell can only be `#`. `won` is the
 * relics the draw gave this player, which is empty until the draw.
 */
export function chartOf(
  seen: TileSet,
  tiles: readonly Terrain[] | null,
  won: readonly Position[],
): string {
  let chart = ''
  for (let cy = 0; cy < CHART_HEIGHT; cy += 1) {
    for (let cx = 0; cx < CHART_WIDTH; cx += 1) {
      chart += cellCode(seen, tiles, won, cx, cy)
    }
  }
  return chart
}

function cellCode(
  seen: TileSet,
  tiles: readonly Terrain[] | null,
  won: readonly Position[],
  cx: number,
  cy: number,
): ChartCode {
  const counts = new Map<Terrain, number>()
  let seenAny = false
  let star = false
  for (let dy = 0; dy < BLOCK; dy += 1) {
    for (let dx = 0; dx < BLOCK; dx += 1) {
      const at = { x: cx * BLOCK + dx, y: cy * BLOCK + dy }
      if (!hasTile(seen, at)) continue
      seenAny = true
      if (tiles === null) continue
      if (won.some((relic) => relic.x === at.x && relic.y === at.y)) star = true
      const terrain = tiles[index(at)]
      if (terrain !== undefined) counts.set(terrain, (counts.get(terrain) ?? 0) + 1)
    }
  }
  if (!seenAny) return '.'
  if (tiles === null) return '#'
  if (star) return '*'
  let best: Terrain | undefined
  let bestCount = 0
  for (const terrain of RARER_FIRST) {
    const count = counts.get(terrain) ?? 0
    // Strictly more: on a tie the earlier, rarer terrain keeps the cell.
    if (count > bestCount) {
      best = terrain
      bestCount = count
    }
  }
  return best === undefined ? '#' : TERRAIN_CODE[best]
}

/** The six rows of squares the text form draws. */
export function chartRows(chart: string): string[] {
  const rows: string[] = []
  for (let cy = 0; cy < CHART_HEIGHT; cy += 1) {
    let row = ''
    for (let cx = 0; cx < CHART_WIDTH; cx += 1) {
      const code = chart[cy * CHART_WIDTH + cx] as ChartCode | undefined
      row += CHART_SQUARES[code ?? '.'] ?? CHART_SQUARES['.']
    }
    rows.push(row)
  }
  return rows
}

// --- the card ----------------------------------------------------------------

export const shareCardStatusSchema = z.enum(['rising', 'sinking', 'finalised'])

// Inside a character class none of the codes needs escaping.
const chartPattern = new RegExp(`^[${CHART_CODES.join('')}]+$`)

/** The numbers the card quotes. Rendered text never appears here. */
export const shareCardSchema = z.object({
  /** The island's name, already rendered in the reader's locale. */
  islandName: z.string(),
  /** The day, already formatted -- "Day 4" in English. */
  dayLabel: z.string(),
  /** Where the island is in its life, which decides what the chart shows. */
  status: shareCardStatusSchema,
  /** Sixty cell codes, row-major (`CHART_CODES`). */
  chart: z.string().length(CHART_CELLS).regex(chartPattern),
  /** Tiles walked on the live day, frozen at finalisation. */
  chartedTiles: z.number().int().nonnegative(),
  /** The island's chartable tiles, which is what the share is of (Q32.4). */
  totalTiles: z.number().int().positive(),
  relicsStaked: z.number().int().nonnegative(),
  relicsWon: z.number().int().nonnegative(),
  lettersSent: z.number().int().nonnegative(),
  lettersReceived: z.number().int().nonnegative(),
  hearsayTiles: z.number().int().nonnegative(),
  /** Letters whose slice the player charted, not tiles. */
  lettersCharted: z.number().int().nonnegative(),
  /** Of those, letters with any tile judged false. */
  liesCaught: z.number().int().nonnegative(),
  /** Islands in the player's Atlas: a tally, not a score. */
  islandsInAtlas: z.number().int().nonnegative(),
  staminaSpent: z.number().int().nonnegative(),
  staminaTotal: z.number().int().positive(),
})
export type ShareCard = z.infer<typeof shareCardSchema>

/** The `guild-voice.json` `ui` keys the card is made of. */
export const SHARE_CARD_UI_KEYS = {
  title: 'shareCardTitle',
  /** Takes `{pct}`. */
  charted: 'shareCharted',
  relicsNone: 'shareRelicsNone',
  /** Takes `{staked}`. */
  relicsPending: 'shareRelicsPending',
  /** Takes `{staked}` and `{won}`. */
  relicsDrawn: 'shareRelicsDrawn',
  hearsayNone: 'shareHearsayNone',
  /** Takes `{letters}`. */
  hearsayPending: 'shareHearsayPending',
  /** Takes `{letters}` and `{lies}`. */
  hearsayJudged: 'shareHearsayJudged',
  letterSent: 'shareLetterSent',
  letterNone: 'shareLetterNone',
  /** Takes `{n}`. */
  islands: 'shareIslands',
  /** The on-screen chart's accessible name; takes `{name}` and `{pct}`. */
  chartAlt: 'shareChartAlt',
  /** The verb. */
  copy: 'copyTheCard',
} as const

/** One sentence of the card: a key, and the arguments its ICU message takes. */
export type ShareCardLine = {
  readonly key: string
  readonly args?: Readonly<Record<string, number>>
}

/** The completion the card quotes, as a whole percentage. */
export function chartedPercent(card: Pick<ShareCard, 'chartedTiles' | 'totalTiles'>): number {
  if (card.totalTiles === 0) return 0
  return Math.round((100 * card.chartedTiles) / card.totalTiles)
}

/**
 * The five sentences, chosen by state.
 *
 * Nothing about the verdict on the player's own letter: that is one bit of
 * the reputation score per day, and the counts behind the score are never
 * shown (Q5.2).
 */
export function shareCardLines(card: ShareCard): ShareCardLine[] {
  const keys = SHARE_CARD_UI_KEYS
  const finalised = card.status === 'finalised'
  const relics: ShareCardLine =
    card.relicsStaked === 0
      ? { key: keys.relicsNone }
      : finalised
        ? { key: keys.relicsDrawn, args: { staked: card.relicsStaked, won: card.relicsWon } }
        : { key: keys.relicsPending, args: { staked: card.relicsStaked } }
  const hearsay: ShareCardLine =
    card.lettersCharted === 0
      ? { key: keys.hearsayNone }
      : finalised
        ? { key: keys.hearsayJudged, args: { letters: card.lettersCharted, lies: card.liesCaught } }
        : { key: keys.hearsayPending, args: { letters: card.lettersCharted } }
  return [
    { key: keys.charted, args: { pct: chartedPercent(card) } },
    relics,
    hearsay,
    { key: card.lettersSent > 0 ? keys.letterSent : keys.letterNone },
    { key: keys.islands, args: { n: card.islandsInAtlas } },
  ]
}

/** The words the card needs, in the reader's locale, already formatted. */
export type ShareCardLabels = {
  readonly title: string
  /** The sentences, in order: what `shareCardLines` resolves to. */
  readonly lines: readonly string[]
  /** The site's address, when the server knows it. */
  readonly footer?: string
}

const CHART_SQUARE_SET: ReadonlySet<string> = new Set(Object.values(CHART_SQUARES))

/** True when every character would survive a paste anywhere (Q7.2, Q32.3). */
export function isAscii(text: string): boolean {
  for (const character of text) {
    const code = character.codePointAt(0) ?? 0
    if (code === 0x0a || (code >= 0x20 && code <= 0x7e)) continue
    if (CHART_SQUARE_SET.has(character)) continue
    return false
  }
  return true
}

/** The card as a block of text. */
export function renderShareCard(card: ShareCard, labels: ShareCardLabels): string {
  const subtitle = `${card.islandName} - ${card.dayLabel}`
  const lines = [labels.title, subtitle, ...chartRows(card.chart), ...labels.lines]
  if (labels.footer !== undefined && labels.footer !== '') lines.push(labels.footer)
  return lines.join('\n')
}
